import { Button, Card, Checkbox, Col, Modal, Result, Row, Space, Tag, message } from 'antd';
import React from 'react';
import ZTable, { IFilterItem, ITableRef, IColumnType } from '@/components/ZTable';
import { useTranslation } from 'react-i18next';
import { ROW_GUTTER } from '@/config';
import { PrintDestinationType, ReceiptPrinterType, RegisterStatus, ShipmentLabelPrinterType, TicketPrinterType } from '@/constants';
import MainContent from '@/layouts/MainContent';
import { Helmet } from 'react-helmet';
import CreateOrUpdateStationModal from './CreateOrUpdateStationModal';
import _ from 'lodash';
import { getFullName } from '@/helpers/string';
import { formatCurrency } from '@/helpers/format';
import CreateBtn from '@/components/app/CreateBtn';
import { defaultApiErrorHandle } from '@/helpers/errorHandler';
import {
  EditOutlined,
  ExclamationCircleOutlined,
  ExclamationOutlined,
  DeleteOutlined,
  ArrowRightOutlined,
  RedoOutlined,
  RetweetOutlined,
} from '@ant-design/icons';
import TicketPaymentRecordModal from './modals/TicketPaymentRecord';
import RegisterReconciliationModal from './modals/RegisterReconciliationModal';
import { PayCircleOutlined } from '@ant-design/icons';
import RechargeAndWithdrawModal from './modals/RechargeAndWithdraw.modal';
import { RegisterDetailModel, RegisterPagingResponseModel } from '@/__generated';
import { apiSdk } from '@/httpClient';

const PAGE_TITLE = 'registerPage_Title';

const filters: Array<IFilterItem> = [
  { type: 'TEXT', mdSize: 8, xlSize: 8, lgSize: 8, xxlSize: 8, name: 'name', label: 'registerPage_Name' },
  {
    type: 'LIST',
    listData: RegisterStatus.__getSelectOptions(),
    mdSize: 8,
    xlSize: 8,
    lgSize: 8,
    xxlSize: 8,
    name: 'status',
    label: 'registerPage_RegisterStatus',
  },
];

const RegistersPage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = React.useState<boolean>(false);

  const zTb = React.useRef<ITableRef>();
  const [openModal, setOpenModal] = React.useState<boolean>(false);
  const [registerId, setRegisterId] = React.useState<string>();
  const [displayTicketPaymentModal, setDisplayTicketPaymentModal] = React.useState(false);
  const [displayReconciliationModal, setDisplayReconciliationModal] = React.useState(false);

  const [registerFullDetail, setRegisterFullDetail] = React.useState<RegisterDetailModel>();
  const [rechargeId, setRechargeId] = React.useState<string>();

  const getRegisterDetail = (id: string | undefined) => {
    if (id) {
      setLoading(true);
      apiSdk.RegisterService.getRegisterDetails({ id: id || '' })
        .then(res => {
          if (res) {
            setRegisterFullDetail(res);
          } else {
            setRegisterFullDetail(undefined);
          }
        })
        .catch(defaultApiErrorHandle)
        .finally(() => {
          setLoading(false);
        });
    } else {
      setRegisterFullDetail(undefined);
    }
  };
  React.useEffect(() => {
    getRegisterDetail(registerId);
  }, [registerId]);

  const columns = React.useMemo(() => {
    const _columns: IColumnType<RegisterPagingResponseModel>[] = [
      {
        title: 'registerPage_Name',
        dataIndex: 'registerName',
        ellipsis: true,
      },
      {
        title: 'registerPage_StationName',
        dataIndex: 'stationName',
        ellipsis: true,
      },
      {
        title: 'registerPage_RegisterStatus',
        dataIndex: 'registerStatus',
        ellipsis: true,
        align: 'center',
        render: v => <Tag color={RegisterStatus.__getColor(v)}>{RegisterStatus.__getValue(v)}</Tag>,
      },

      {
        title: 'zTable.actions',
        dataIndex: 'id',
        width: 100,
        ellipsis: true,
        align: 'center',
        fixed: 'left',
        render: (v, r) => (
          <Button
            size="small"
            shape="circle"
            icon={<ArrowRightOutlined />}
            type="primary"
            onClick={() => {
              setRegisterId(r.id);
            }}
          />
        ),
      },
    ];
    return _columns;
  }, []);

  const onDelete = (id: string) => {
    Modal.confirm({
      okText: t('Delete'),
      cancelText: t('Cancel'),
      title: t('Are you sure want to delete this item?'),
      icon: <DeleteOutlined />,
      onOk: () => {
        setLoading(true);
        apiSdk.RegisterService.delete({ id: id })
          .then(res => {
            if (res.success) {
              setRegisterId(undefined);

              zTb.current?.reload();
            } else {
              defaultApiErrorHandle(res);
            }
          })
          .catch(defaultApiErrorHandle)
          .finally(() => {
            setLoading(false);
          });
      },
    });
  };
  const onEdit = (id: string) => {
    setOpenModal(true);
    setRegisterId(id);
  };

  const onClose = (reload?: boolean) => {
    setOpenModal(false);
    if (reload) {
      zTb.current?.reload();
      getRegisterDetail(registerId);
    }
  };

  const changeStatus = (status: any) => {
    if (registerFullDetail?.id) {
      apiSdk.RegisterService.updateStatus({
        body: {
          id: registerFullDetail.id,
          status: status,
        },
      })
        .then(res => {
          if (res.success) {
            getRegisterDetail(registerFullDetail?.id);
            zTb.current?.reload();
          } else {
            defaultApiErrorHandle(res);
          }
        })
        .catch(defaultApiErrorHandle);
    }
  };
  const resetPin = (cashierId: string) => {
    Modal.confirm({
      title: t('registerPage_ResetPinConfirm'),
      onOk: () => {
        setLoading(true);
        apiSdk.RegisterService.resetPin({ cashierId: cashierId, registerId: registerFullDetail?.id || '' })
          .then(res => {
            if (res.success) {
              message.success(t('registerPage_ResetPinSuccess'));
            } else {
              defaultApiErrorHandle(res);
            }
          })
          .catch(defaultApiErrorHandle)
          .finally(() => {
            setLoading(false);
          });
      },
    });
  };
  return (
    <MainContent
      isLoading={loading}
      title={t(PAGE_TITLE)}
      extraBtns={
        <CreateBtn
          onClick={() => {
            setOpenModal(true);
            setRegisterId(undefined);
          }}
        />
      }
    >
      <Helmet title={t(PAGE_TITLE)} />
      <div className="ant-layout-content-body">
        <Row gutter={ROW_GUTTER}>
          <Col md={12}>
            <ZTable<RegisterPagingResponseModel>
              columns={columns}
              primaryKey="id"
              tableRef={zTb}
              url="/registers/cashier-registers/get-registers"
              bordered
              order_by="createdOn"
              order_by_asc={true}
              allowEdit={false}
              allowDelete={false}
              delete_url="/registers/cashier-registers/delete/"
              filters={filters}
              searchBtnAlignLeft
              rowClassName={record => (record.id === registerId ? 'antd-row-selected' : '')}
            ></ZTable>
          </Col>
          <Col md={12}>
            {registerFullDetail ? (
              <Card
                extra={
                  <div style={{ textAlign: 'right' }}>
                    <Space>
                      {registerFullDetail.registerStatus === RegisterStatus.Open ? (
                        <Button onClick={() => setDisplayReconciliationModal(true)} type="default" icon={<PayCircleOutlined />}>
                          {t('registerReconciliation_Title')}
                        </Button>
                      ) : null}

                      <Button onClick={() => onEdit(registerFullDetail.id || '')} type="primary" icon={<EditOutlined />}>
                        {t('zTable.edit')}
                      </Button>
                      <Button type="primary" onClick={() => onDelete(registerFullDetail.id || '')} danger icon={<ExclamationCircleOutlined />}>
                        {t('zTable.delete')}
                      </Button>
                      <Button
                        type="primary"
                        onClick={() => setRechargeId(registerFullDetail.id || '')}
                        icon={<RetweetOutlined />}
                        style={{ backgroundColor: '#088766' }}
                      >
                        {t('registerPage_RechargeWithdraw')}
                      </Button>
                    </Space>
                  </div>
                }
                className="mtopo-card"
                title={registerFullDetail.registerName}
              >
                <table style={{ width: '100%' }} className="app-simple-table">
                  <tbody>
                    <tr>
                      <td style={{ width: 200 }}>
                        <span>{t('registerPage_RegisterStatus')}</span>
                      </td>
                      <td>
                        <b>
                          <Tag color={RegisterStatus.__getColor(registerFullDetail.registerStatus)} style={{ marginRight: 20 }}>
                            {RegisterStatus.__getValue(registerFullDetail.registerStatus)}
                          </Tag>
                          {registerFullDetail.registerStatus === RegisterStatus.Ready ? (
                            <Button
                              onClick={() => {
                                changeStatus(RegisterStatus.Closed);
                              }}
                              type="link"
                              danger
                              size="small"
                              icon={<RedoOutlined />}
                            >
                              {t('registerPage_ChangeToClose')}
                            </Button>
                          ) : null}
                          {registerFullDetail.registerStatus === RegisterStatus.Closed ? (
                            <Button
                              onClick={() => {
                                changeStatus(RegisterStatus.Ready);
                              }}
                              type="link"
                              size="small"
                              icon={<RedoOutlined />}
                            >
                              {t('registerPage_ChangeToReady')}
                            </Button>
                          ) : null}
                          {registerFullDetail.registerStatus === RegisterStatus.Open ? (
                            <Button
                              onClick={() => {
                                changeStatus(RegisterStatus.Ready);
                              }}
                              type="link"
                              size="small"
                              icon={<RedoOutlined />}
                            >
                              {t('registerPage_ChangeToReady')}
                            </Button>
                          ) : null}
                        </b>
                      </td>
                    </tr>
                    <tr>
                      <td style={{ width: 200 }}>
                        <span>{t('registerPage_Cashiers')}</span>
                      </td>
                      <td>
                        <b>
                          {registerFullDetail.cashiers?.map(cashier => {
                            return (
                              <div style={{ display: 'flex', alignItems: 'center' }}>
                                <div style={{ minWidth: 200 }}>{getFullName(cashier.fistName, cashier.lastName)}</div>
                                <Button
                                  onClick={() => {
                                    resetPin(cashier.id || '');
                                  }}
                                  type="link"
                                  danger
                                  size="middle"
                                  icon={<RedoOutlined />}
                                >
                                  {t('registerPage_ResetPin')}
                                </Button>
                              </div>
                            );
                          })}
                        </b>
                      </td>
                    </tr>
                    <tr>
                      <td style={{ width: 200 }}>
                        <span>{t('registerPage_OpeningHours')}</span>
                      </td>
                      <td>
                        <b>{[registerFullDetail.openingHoursFrom, registerFullDetail.openingHoursTo].filter(x => x).join(' - ')}</b>
                      </td>
                    </tr>
                    <tr>
                      <td style={{ width: 200 }}>
                        <span>{t('registerPage_StartingCash')}</span>
                      </td>
                      <td>
                        <b>{formatCurrency(registerFullDetail.startingCash)}</b>
                      </td>
                    </tr>
                    <tr>
                      <td style={{ width: 200 }}>
                        <span>{t('registerPage_CurrentBalance')}</span>
                      </td>
                      <td>
                        <b>{formatCurrency(registerFullDetail.currentBalance)}</b>
                      </td>
                    </tr>
                    {registerFullDetail ? (
                      <>
                        <tr>
                          <td style={{ width: 200 }}>
                            <span>{t('registerPage_PrintDestination')}</span>
                          </td>
                          <td>
                            <b>{PrintDestinationType.__getValue(registerFullDetail.printDestination)}</b>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ width: 200 }}>
                            <span>{t('registerPage_ReceiptDefaultPrinter')}</span>
                          </td>
                          <td>
                            <b>{ReceiptPrinterType.__getValue(registerFullDetail.receiptDefaultPrinter)}</b>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ width: 200 }}>
                            <span>{t('registerPage_TicketDefaultPrinter')}</span>
                          </td>
                          <td>
                            <b>{TicketPrinterType.__getValue(registerFullDetail.ticketDefaultPrinter)}</b>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ width: 200 }}>
                            <span>{t('registerPage_ShipmentLabelDefaultPrinter')}</span>
                          </td>
                          <td>
                            <b>{ShipmentLabelPrinterType.__getValue(registerFullDetail.shipmentLabelDefaultPrinter)}</b>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ width: 200 }}>
                            <span>{t('registerPage_loyaltyClient')}</span>
                          </td>
                          <td>
                            <Checkbox checked={registerFullDetail.loyaltyClient} />
                          </td>
                        </tr>
                        <tr>
                          <td style={{ width: 200 }}>
                            <span>{t('registerPage_ActionAnnulation')}</span>
                          </td>
                          <td>
                            <Checkbox checked={registerFullDetail.actionAnnulation} />
                          </td>
                        </tr>
                      </>
                    ) : null}
                  </tbody>
                </table>
                <Space style={{ marginTop: 20 }}>
                  <Button
                    type="primary"
                    onClick={() => {
                      setDisplayTicketPaymentModal(true);
                    }}
                  >
                    {t('registerPage_TicketPayments')}
                  </Button>
                </Space>
              </Card>
            ) : (
              <Card size="small">
                <Result icon={<ExclamationOutlined style={{ color: 'gray' }} />} subTitle="Please select the register" />
              </Card>
            )}
          </Col>
        </Row>
      </div>
      {openModal ? <CreateOrUpdateStationModal registerId={registerId} onClose={onClose} /> : null}

      {registerId && displayTicketPaymentModal ? (
        <TicketPaymentRecordModal onClose={() => setDisplayTicketPaymentModal(false)} registerId={registerId} />
      ) : null}

      {registerId && registerFullDetail && displayReconciliationModal ? (
        <RegisterReconciliationModal onClose={() => setDisplayReconciliationModal(false)} registerId={registerId} registerInfo={registerFullDetail} />
      ) : null}
      {rechargeId ? (
        <RechargeAndWithdrawModal
          onClose={reload => {
            if (reload) {
              zTb.current?.reload();
              getRegisterDetail(rechargeId);
            }
            setRechargeId(undefined);
          }}
          registerId={rechargeId}
        />
      ) : null}
    </MainContent>
  );
};
export default RegistersPage;
